import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageBus } from '../../services/message/message-bus.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { RecaptchaComponent } from '../core/controls/recaptcha.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';

@Component({
  selector: 'app-forgot-password',
  imports: [
    InputComponent,
    ButtonComponent,
    RouterModule,
    ActionRowComponent,
    RecaptchaComponent,
    ApiErrorLabelComponent,
    ReactiveFormsModule,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <form
      class="tw-px-4 tw-pt-4 tw-pb-2 "
      [formGroup]="fg"
      (ngSubmit)="sendCode()"
    >
      <h2 class="tw-text-base tw-font-bold tw-py-4 tw-break-words">
        Forgot your password?
      </h2>
      <div class="tw-break-words">
        Complete the information below and a one-time code will be emailed to
        you.
      </div>

      <app-required-fields-note />

      @if (error) {
        <div class="tw-flex tw-justify-center tw-pt-4">
          <app-api-error-label [error]="error" />
        </div>
      }

      @if (recaptchaLoadError) {
        <div class="tw-flex tw-justify-center tw-pt-4">
          <app-api-error-label
            error="Unable to load security verification. Please refresh or contact technical support."
          />
        </div>
      }

      <div class="tw-pt-8 tw-pb-2">
        <app-input
          [control]="fg.controls.userName"
          label="Username"
          icon="fa-light fa-user"
          [large]="true"
        ></app-input>
      </div>
      @if (recaptchaSiteKey) {
        <div class="tw-pt-4 tw-pb-8 sm:tw-px-2">
          <app-recaptcha
            [control]="fg.controls.recaptcha"
            [siteKey]="recaptchaSiteKey"
          />
        </div>
      }
      <div class="tw-pb-10">
        <app-action-row>
          <app-button
            (click)="sendCode()"
            right2
            [disabled]="!fg.valid || recaptchaLoadError"
            label="Send Code"
            [loading]="loading"
          />
          <app-button
            routerLink="/login"
            emphasis="Low"
            right3
            label="Cancel"
          />
        </app-action-row>
      </div>
    </form>
  `,
  styles: ``,
})
export class ForgotPasswordComponent extends BaseComponent implements OnInit {
  protected messagBus = inject(MessageBus);

  loading = false;
  error: any;
  recaptchaSiteKey: string | null = null;
  recaptchaLoadError = false;

  fg = new FormGroup({
    userName: new FormControl<string>(null, Validators.required),
    recaptcha: new FormControl<string>(null, Validators.required),
  });

  async ngOnInit() {
    try {
      const config = await this.api.auth.getRecaptchaSiteKey();
      this.recaptchaSiteKey = config?.siteKey ?? null;
    } catch {
      this.recaptchaLoadError = true;
    }
  }

  async sendCode() {
    this.loading = true;
    this.error = null;
    try {
      await this.api.auth.getXsrf();
      const passwordResult = await this.api.auth.forgotPassword({
        userName: this.fg.value.userName,
        recaptcha: this.fg.value.recaptcha,
      });

      if (passwordResult.result === 'Success') {
        this.router.navigate(['/login/otp'], {
          queryParams: {
            profile: 'forgotPassword',
          },
          state: {
            otp: passwordResult,
          },
        });
      } else {
        this.error =
          'The system could not validate your information.  Make sure your user name and password are correct and try again.';
      }
    } catch (e) {
      this.error = e;
    } finally {
      this.loading = false;
    }
  }
}
